import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { sumBy, uniq, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { getOrderDisplayNumber } from '../domain/order';

// Shows the invoice's order number(s), plus a combined shipment/cargo tracking summary (recorded
// via the emailhandler xlsx-attachment pipeline): total boxes, parcels, pallets across all orders,
// with a link to expand the full per-order breakdown table. If none of the orders have shipment
// info recorded yet, an explicit "not available" indication is shown instead, so the invoicing
// team can tell at a glance whether this data exists rather than seeing nothing. This is purely a
// view-only convenience. Used in both invoiceview.jsx and invoiceeditview.jsx.
//
// When `embedded` is true, the component is assumed to already be rendered inside its own
// collapsible container (e.g. an Accordion) - in that case it skips its own show/hide toggle and
// renders the detail table directly, so there is only a single collapsible control on screen.
export default function OrdersWithShipmentInfo({ orders: ordersProp, embedded = false }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const orders = uniqBy(ordersProp || [], 'orderNumber');
    const ordersWithShipmentInfo = orders.filter((o) => !!o.shipmentInfo);

    const palletsCellValue = (o) => {
        if (o.shipmentInfo.groupedWithOrderNumber) {
            return t('invoice.palletsSharedWithOrder', {
                orderNumber: o.shipmentInfo.groupedWithOrderDisplayNumber || o.shipmentInfo.groupedWithOrderNumber,
            });
        }
        const eur = o.shipmentInfo.palletsEur != null ? o.shipmentInfo.palletsEur : 0;
        const teho = o.shipmentInfo.palletsTeho != null ? o.shipmentInfo.palletsTeho : 0;
        if (!eur && !teho) return '-';
        const parts = [];
        if (eur) parts.push(`${eur} ${t('invoice.palletsEur')}`);
        if (teho) parts.push(`${teho} ${t('invoice.palletsTeho')}`);
        return parts.join(', ');
    };

    // Pallets are only summed from non-grouped (lead) orders - grouped/satellite orders show the
    // same lead order's pallet counts for display purposes only, so including them here would
    // double-count the physical pallets.
    const leadOrders = ordersWithShipmentInfo.filter((o) => !o.shipmentInfo.groupedWithOrderNumber);
    const totalBoxes = sumBy(ordersWithShipmentInfo, (o) => o.shipmentInfo.boxes || 0);
    const totalParcels = sumBy(ordersWithShipmentInfo, (o) => o.shipmentInfo.parcels || 0);
    const totalPalletsEur = sumBy(leadOrders, (o) => o.shipmentInfo.palletsEur || 0);
    const totalPalletsTeho = sumBy(leadOrders, (o) => o.shipmentInfo.palletsTeho || 0);
    const providers = uniq(ordersWithShipmentInfo.map((o) => o.shipmentInfo.providerName));

    const summaryParts = [];
    if (totalBoxes) summaryParts.push(`${totalBoxes} ${t('invoice.boxes')}`);
    if (totalParcels) summaryParts.push(`${totalParcels} ${t('invoice.parcels')}`);
    if (totalPalletsEur) summaryParts.push(`${totalPalletsEur} ${t('invoice.palletsEur')}`);
    if (totalPalletsTeho) summaryParts.push(`${totalPalletsTeho} ${t('invoice.palletsTeho')}`);

    return (
        <React.Fragment>
            <Grid item xs={12} sm={6}>
                <span style={{ fontWeight: 'bold' }}>{t('general.orderNumber')}</span>
            </Grid>
            <Grid item xs={12} sm={6}>
                <span>{orders.map((o) => getOrderDisplayNumber(o)).join(', ')}</span>
            </Grid>
            {ordersWithShipmentInfo.length > 0 ? (
                <Grid item xs={12}>
                    <div>
                        <LocalShippingIcon fontSize="small" sx={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        <span>
                            {t('invoice.shipmentSummary', {
                                orderCount: ordersWithShipmentInfo.length,
                                providers: providers.join(', '),
                                summary: summaryParts.join(', '),
                            })}
                        </span>
                        {!embedded && (
                            <>
                                {' '}
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => setExpanded((current) => !current)}>
                                    {expanded ? t('invoice.hideShipmentDetails') : t('invoice.showShipmentDetails')}
                                </Link>
                            </>
                        )}
                    </div>
                    <Collapse in={embedded || expanded}>
                        <Table size="small" sx={{ maxWidth: 600 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('general.orderNumber')}</TableCell>
                                    <TableCell>{t('invoice.cargoProvider')}</TableCell>
                                    <TableCell>{t('invoice.boxes')}</TableCell>
                                    <TableCell>{t('invoice.parcels')}</TableCell>
                                    <TableCell>{t('invoice.pallets')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ordersWithShipmentInfo.map((o) => (
                                    <TableRow key={o.orderNumber}>
                                        <TableCell>{getOrderDisplayNumber(o)}</TableCell>
                                        <TableCell>{o.shipmentInfo.providerName}</TableCell>
                                        <TableCell>{o.shipmentInfo.boxes != null ? o.shipmentInfo.boxes : '-'}</TableCell>
                                        <TableCell>{o.shipmentInfo.parcels != null ? o.shipmentInfo.parcels : '-'}</TableCell>
                                        <TableCell>{palletsCellValue(o)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Collapse>
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontStyle: 'italic' }}>
                        <LocalShippingIcon
                            fontSize="small"
                            sx={{ verticalAlign: 'middle', marginRight: '4px', opacity: 0.6 }}
                        />
                        {t('invoice.noShipmentInfo')}
                    </span>
                </Grid>
            )}
        </React.Fragment>
    );
}

